import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Users, UserCheck, Building2, Award, Eye, X, ChevronDown, 
  User, Search, ArrowRight, GraduationCap, Anchor, ClipboardCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const SuperAdminMode = ({ onImpersonate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = [
    { id: 'candidat', label: 'Candidats', icon: User, color: 'red', endpoint: '/candidats/liste' },
    { id: 'stagiaire', label: 'Stagiaires', icon: Users, color: 'blue', endpoint: '/stagiaires' },
    { id: 'formateur', label: 'Formateurs', icon: Award, color: 'green', endpoint: '/formateurs' },
    { id: 'tuteur', label: 'Tuteurs', icon: UserCheck, color: 'purple', endpoint: '/tuteurs' },
    { id: 'entreprise', label: 'Entreprises', icon: Building2, color: 'orange', endpoint: '/entreprises' },
    { id: 'responsable_pedagogique', label: 'Resp. Péda', icon: GraduationCap, color: 'teal', endpoint: '/responsables-pedagogiques' },
    { id: 'evaluateur', label: 'Évaluateurs', icon: ClipboardCheck, color: 'indigo', endpoint: '/evaluateurs' },
    { id: 'moniteur', label: 'Moniteurs VNM', icon: Anchor, color: 'amber', endpoint: '/moniteurs' }
  ];

  // Réinitialiser la sélection quand on ouvre le menu
  const handleToggle = () => {
    if (!isOpen) {
      // On ouvre le menu -> réinitialiser
      setSelectedRole(null);
      setUsers([]);
      setSearchTerm('');
    }
    setIsOpen(!isOpen);
  };

  const loadUsers = async (role) => {
    setLoading(true);
    try {
      const roleConfig = roles.find(r => r.id === role);
      const response = await axios.get(roleConfig.endpoint);
      setUsers(response.data || []);
    } catch (error) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    loadUsers(roleId);
    setSearchTerm('');
  };

  const handleImpersonate = (targetUser) => {
    const userInfo = {
      user_id: targetUser.user_id || targetUser.moniteur_id || targetUser.evaluateur_id,
      email: targetUser.user_info?.email || targetUser.email,
      role: selectedRole,
      nom: targetUser.user_info?.nom || targetUser.nom || targetUser.nom_entreprise || '',
      prenom: targetUser.user_info?.prenom || targetUser.prenom || ''
    };
    
    toast.success(`Mode Super Admin activé : ${userInfo.prenom} ${userInfo.nom} (${selectedRole})`);
    onImpersonate(userInfo);
    setIsOpen(false);
  };

  const filteredUsers = users.filter(u => {
    const nom = u.user_info?.nom || u.nom || u.nom_entreprise || '';
    const prenom = u.user_info?.prenom || u.prenom || '';
    const email = u.user_info?.email || u.email || '';
    const searchLower = searchTerm.toLowerCase();
    return nom.toLowerCase().includes(searchLower) || 
           prenom.toLowerCase().includes(searchLower) ||
           email.toLowerCase().includes(searchLower);
  });

  const getColorClasses = (color) => ({
    red: 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200',
    blue: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
    green: 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200',
    orange: 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200',
    teal: 'bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200'
  }[color]);

  return (
    <div className="relative">
      <Button
        onClick={handleToggle}
        className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
        data-testid="super-admin-button"
      >
        <Shield className="h-4 w-4 mr-2" />
        Mode Super Admin
        <ChevronDown className={`h-4 w-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="fixed inset-0 z-50 sm:absolute sm:inset-auto sm:top-full sm:right-0 sm:mt-2">
          {/* Overlay mobile */}
          <div className="fixed inset-0 bg-black/30 sm:hidden" onClick={() => setIsOpen(false)} />
          <div className="fixed bottom-0 left-0 right-0 sm:relative sm:bottom-auto sm:w-80 md:w-96 bg-white border border-slate-200 rounded-t-2xl sm:rounded-xl shadow-2xl overflow-hidden max-h-[80vh] sm:max-h-[70vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                <span className="font-semibold">Mode Super Admin</span>
              </div>
              <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-white/80 mt-1">Accédez à n'importe quel espace utilisateur</p>
          </div>

          {/* Role Selection */}
          {!selectedRole ? (
            <div className="p-4">
              <p className="text-sm text-slate-600 mb-3">Sélectionnez un type d'utilisateur :</p>
              <div className="grid grid-cols-2 gap-2">
                {roles.map(role => {
                  const Icon = role.icon;
                  return (
                    <button
                      key={role.id}
                      onClick={() => handleRoleSelect(role.id)}
                      className={`p-3 rounded-lg border ${getColorClasses(role.color)} transition-all flex items-center gap-2`}
                      data-testid={`select-role-${role.id}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium text-sm">{role.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="p-4">
              {/* Back button */}
              <button
                onClick={() => { setSelectedRole(null); setUsers([]); }}
                className="text-sm text-slate-500 hover:text-slate-700 mb-3 flex items-center gap-1"
              >
                ← Retour aux rôles
              </button>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              {/* User List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {loading ? (
                  <div className="text-center py-4 text-slate-500">Chargement...</div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-4 text-slate-500">Aucun utilisateur trouvé</div>
                ) : (
                  filteredUsers.map(u => {
                    const nom = u.user_info?.nom || u.nom || u.nom_entreprise || '';
                    const prenom = u.user_info?.prenom || u.prenom || '';
                    const email = u.user_info?.email || u.email || '';
                    
                    return (
                      <button
                        key={u.user_id}
                        onClick={() => handleImpersonate(u)}
                        className="w-full p-3 bg-slate-50 hover:bg-amber-50 border border-slate-200 hover:border-amber-300 rounded-lg flex items-center justify-between transition-all group"
                        data-testid={`impersonate-${u.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-medium">
                            {(prenom[0] || nom[0] || '?').toUpperCase()}
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-slate-800">{prenom} {nom}</p>
                            <p className="text-xs text-slate-500">{email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye className="h-4 w-4" />
                          <ArrowRight className="h-4 w-4" />
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminMode;
